import { Component, Input, OnInit } from '@angular/core';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { PictorialChartEnum } from '@shared/enums/pictorial-chart.enum';
import { WidgetStatePlaceholderEnum } from '@shared/enums/widget-state-placeholder.enum';

import { CustomEventService } from '@shared/services/custom-event.service';
import { GlobalSettingsService } from '@shared/services/global-settings.service';
import { WidgetService } from '@shared/services/widget.service';

import GenerateDataZoomUtil from '@shared/utils/data-zoom.util';
import DateUtil from '@shared/utils/date.util';

import colorMap from '@shared/variables/chart-color.variable';
import { commonChartConfig, commonChartTooltip } from '@shared/variables/echart-common.variable';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EChartOption, ECharts } from 'echarts';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-pictorial-widget',
  templateUrl: './pictorial-widget.component.html'
})
export class PictorialWidgetComponent implements OnInit {

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  widgetType: string;
  headerAddon: string;
  headerComponents: [] = [];
  widgetFooter: Widget.FooterModel;
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  @Input()
  set period(period: string) {
    if (period === 'FROM') {
      this.selectedDate = this.from.toString(10);
    } else if (period === 'TODAY') {
      this.selectedDate = `${period}`;
    } else if (period) {
      this.selectedDate = `FROM_LAST_${period}`;
    }
  };
  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  graphData: any;
  echartsColors: string[];

  // execution chart
  pictorialChartOption: EChartOption;
  pictorialChartInstance: ECharts;
  pictorialWidgetUrls: string[];

  // total chart
  pieChartOption: EChartOption;
  pieChartInstance: ECharts;
  selectedLegendData: object;

  dateFormat: string;
  toolTipHTML: string;
  currentTabIndex = 0;

  constructor(private customEventService: CustomEventService,
              private widgetService: WidgetService,
              private globalSettingsService: GlobalSettingsService) { }

  ngOnInit() {
    this.dateFormat = this.globalSettingsService.dateTimeFormat;

    this.toolTipHTML = this.headerComponents.join('<br>');

    this.loadWidgetData(true);

    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }

    });
  }

  loadWidgetData(initial?: boolean): void {
    this.loading = true;

    if (initial) {
      this.initialLoading = true;
    }

    this.widgetService.get(this.widgetId, this.selectedDate)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.loading = false;
        this.initialLoading = false;

        this.selectedLegendData = undefined;

        if (!response.data) {
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
          return;
        }
        this.showWidgetStatePlaceholder = undefined;

        if (response.footer) {
          this.widgetFooter = response.footer;
        }

        this.echartsColors = response.data.length > 5 ? colorMap.get('default') : colorMap.get('short');

        if (initial) {
          this.pictorialChartOption = {
            backgroundColor: commonChartConfig.bg,
            color: this.echartsColors
          };
        }

        let dataIndexArray = [];

        const { data: { projectInfo, legend } } = response;
        let { data: { series } } = response;

        this.graphData = response;

        if (!series.length) {
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_AVAILABLE;
          return;
        }

        this.showWidgetStatePlaceholder = undefined;
        this.pictorialWidgetUrls = series.map(item => item.url ? item.url : '');

        const sumTotalCount = (index: number) => {
          return series
            .map(item => item.data[index])
            .reduce((sum, value) => sum + value);
        };

        this.graphData.total = [];

        series[0].data.forEach((item, index) => {
          this.graphData.total.push({
            key: PictorialChartEnum[index],
            value: sumTotalCount(index)
          });
        });

        this.graphData.total = this.graphData.total.reverse();

        if (!initial && this.currentTabIndex === 1) {
          this.updatePieChart(this.getPieChartData());
        }

        series = series.map((item, index) => {
          dataIndexArray.push(item.dataIndex);
          return {
            ...item,
            index
          }
        });

        dataIndexArray = Array.from(new Set(dataIndexArray));

        const xAxisData = series.map(item => {
          return legend[item.dataIndex];
        });

        const legendData = Object.values(legend).map((item: string, index) => {
          return {
            name: item,
            color: colorMap.get('pictorial')[index],
            icon: 'circle'
          };
        });

        const rich = Object.keys(legend).reduce((acc, curr, index) => {
          return {
            ...acc,
            [curr]: {
              height: 5,
              width: 20,
              backgroundColor: colorMap.get('pictorial')[index]
            }
          };
        }, {});

        const title = [
          {
            subtext: `${projectInfo.start}`,
            left: '10',
            bottom: '40'
          },
          {
            subtext: `{value|${projectInfo.executions}} {text|Executions}`,
            left: 'center',
            bottom: '35',
            subtextStyle: {
              rich: {
                value: {
                  fontSize: 20,
                  color: '#000',
                  height: 8
                },
                text: {
                  height: 8,
                  verticalAlign: 'bottom'
                }
              }
            }
          },
          {
            subtext: `${projectInfo.end}`,
            right: '10',
            bottom: '40'
          }
        ];

        this.pictorialChartOption = {
          title,
          grid: {
            top: 30,
            bottom: 85,
            left: 10,
            right: 10
          },
          legend: {
            type: 'scroll',
            data: legendData,
            textStyle: {
              color: commonChartConfig.textColor
            },
            top: 0
          },
          dataZoom: GenerateDataZoomUtil.generateDataZoom({
            dataCount: series.length,
            orient: 'horizontal',
            showDataShadow: false,
            x: 15
          }),
          tooltip: {
            ...commonChartTooltip,
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            },
            formatter: (params): string => {
              return (params as any[]).map((item, index, array) => {
                let returnValue = '';
                if (index === 1) {
                  const date = series[item.dataIndex] && series[item.dataIndex].date && series[item.dataIndex].date !== 0
                    ? DateUtil.formatDate(+series[item.dataIndex].date, this.dateFormat) + '</br>'
                    : '';

                  returnValue = `${date}${item.axisValue}</br>`;
                }
                if (index === 1 && series[item.dataIndex].executionTime && series[item.dataIndex].executionTime.length) {
                  returnValue = `${returnValue}${series[item.dataIndex].executionTime} </br>`
                }
                if (item.value !== 0) {
                  returnValue = returnValue + `${item.marker} ${item.value} </br>`;
                }
                if(index === array.length - 1 && series[item.dataIndex].environment && series[item.dataIndex].environment.length) {
                  returnValue = `${returnValue}${series[item.dataIndex].environment.join('</br>')}`
                }
                return returnValue;
              }).join('');
            }
          },
          xAxis: {
            type: 'category',
            data: xAxisData,
            show: true,
            axisLine: {
              show: false
            },
            splitLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              formatter: (value) => {
                return [
                  `{${Object.values(legend).findIndex(item => value === item)}|}`
                ].join('\n');
              },
              rich
            }
          },
          yAxis: {
            type: 'value',
            show: false,
            splitLine: {
              show: false
            },
            axisLine: {
              show: false
            },
            axisLabel: {
              show: false
            }
          },
          series: this.generatePictorialBarData(series, legend, colorMap.get('pictorial'), dataIndexArray),
          animation: true
        };

      }, error => {
        console.log('error while fetching widget data', error);
        this.loading = false;
        this.initialLoading = false;
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
      });
  }

  generatePictorialBarData(series: any[], legend: any, projectColors: any[], dataIndexArray: number[]): any[] {
    let data = [];

    const generateEmptyArray = (len = series.length) => Array.from(Array(len), _ => 0);

    const initialData = generateEmptyArray();

    const addSeriesData = (generalData, successData, warningData, failData, errorData) => {
      return [
        generalData,
        {
          ...generalData,
          data: successData,
          itemStyle: {
            normal: {
              color: colorMap.get('status')[3]
            }
          }
        },
        {
          ...generalData,
          data: warningData,
          itemStyle: {
            normal: {
              color: colorMap.get('status')[2]
            }
          }
        },
        {
          ...generalData,
          data: failData,
          itemStyle: {
            normal: {
              color: colorMap.get('status')[1]
            }
          }
        },
        {
          ...generalData,
          data: errorData,
          itemStyle: {
            normal: {
              color: colorMap.get('status')[0]
            }
          }
        }
      ];
    };

    const commonSeriesData = {
      data: initialData,
      type: 'bar',
      symbol: 'rect',
      symbolSize: [20, 8],
      barWidth: 20,
      itemStyle: {
        normal: {
          barBorderRadius: 52
        }
      },
      stack: 'all',
      symbolRepeat: true,
      animation: false
    };

    dataIndexArray.forEach((dataIndexItem) => {
      const filteredSeries = series.filter(item => item.dataIndex === dataIndexItem);
      const successData = generateEmptyArray();
      const warningData = generateEmptyArray();
      const failData = generateEmptyArray();
      const errorData = generateEmptyArray();

      filteredSeries.forEach((item) => {
        successData[item.index] = item.data[3];
        warningData[item.index] = item.data[2];
        failData[item.index] = item.data[1];
        errorData[item.index] = item.data[0];
      });

      const generalData = {
        ...commonSeriesData,
        name: legend[dataIndexItem],
        itemStyle: {
          normal: {
            color: projectColors[dataIndexItem]
          }
        }
      };

      data = [
        ...data,
        ...addSeriesData(generalData, successData, warningData, failData, errorData)
      ];
    });

    return data;
  }

  pieChartCreator(): void {
    const series = this.getPieChartData();

    const legendData = [...this.graphData.total]
      .map(data => {
        return {
          name: data.key,
          icon: 'circle'
        };
      });

    this.pieChartOption = {
      ...this.pieChartOption,
      color: colorMap.get('status'),
      tooltip: {
        ...commonChartTooltip,
        formatter: '{a} <br/>{b} : {c} ({d}%)'
      },
      legend: {
        type: 'scroll',
        data: legendData.reverse(),
        textStyle: {
          color: commonChartConfig.textColor
        }
      },
      series: [
        {
          name: this.widgetName,
          type: 'pie',
          radius: '70%',
          center: ['50%', '60%'],
          data: series.reverse(),
          itemStyle: {
            normal: {
              borderWidth: 0.5,
              borderColor: 'white'
            },
            emphasis: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: commonChartConfig.itemHoverShadowColor
            }
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          }
        }
      ]
    };
  }

  updatePieChart(data): void {
    const series: any = this.pieChartOption.series;

    if (this.pieChartInstance && series) {
      series[0].data = data.reverse();

      this.pieChartInstance.setOption({
        series
      });
    }
  }

  onChartEvent($event): void {
    if (this.pictorialWidgetUrls && this.pictorialWidgetUrls[$event.dataIndex]) {
      window.open(this.pictorialWidgetUrls[$event.dataIndex], '_blank');
    }
  }

  onChartInit($event, type: 'pictorial' | 'pie'): void {
    if (type === 'pictorial') {
      this.pictorialChartInstance = $event;
      return;
    }
    this.pieChartInstance = $event;
  }

  onLegendEvent($event): void {
    if (this.graphData.data.series && this.graphData.data.series.length) {
      const sumFilteredSeries = (index: number): number =>
        this.graphData.data.series
          .filter(item => $event.selected && $event.selected[item.name])
          .map(item => item.data[index])
          .reduce((sum, value) => sum + value, 0);
      this.graphData.total = this.graphData.total.map(item => {
        return {
          ...item,
          value: sumFilteredSeries(+PictorialChartEnum[item.key])
        };
      });
      this.selectedLegendData = $event.selected;
    }
  }

  changeTab(index: number): void {
    this.currentTabIndex = index;

    if (this.currentTabIndex === 1 && !this.pieChartInstance) {
      this.pieChartCreator();
    }

    if (this.currentTabIndex === 1 && this.pieChartInstance) {
      this.updatePieChart(this.getPieChartData());
    }

    if (this.currentTabIndex === 0 && this.selectedLegendData) {
      this.pictorialChartOption.legend.selected = this.selectedLegendData;
    }
  }

  getPieChartData(): Array<{name: string, value: number}> {
    return this.graphData.total
      .map(item => {
        return {
          name: item.key,
          value: item.value
        };
      });
  }
}
